import { CommonChapter, CommonSkillField, CommonStudent, CommonWorkedChapter } from '@codidae/common-types';
import chevronLeft from 'assets/icons/back-chevron-left.svg';
import ChildrenChapterBubble from 'components/children-chapter-bubble/children-chapter-bubble';
import { useContext, useState } from 'react';
import { getColorFromFieldId } from 'utils/colors';
import { getMonthName } from 'utils/date';
import haptic from 'utils/haptic';
import { ChildrenHeaderContext } from 'views/children/dashboard';
import styles from './chapters-path.module.scss';

export default function ChaptersPath({
  selectedFieldId,
  allFields,
  onBack,
  child,
  chapters,
  workedChapters,
  isParent,
}: {
  selectedFieldId: number;
  allFields: CommonSkillField[];
  onBack?: VoidFunction;
  child: CommonStudent;
  chapters: CommonChapter[];
  workedChapters: CommonWorkedChapter[];
  isParent?: boolean;
}) {
  const { setColor } = useContext(ChildrenHeaderContext);
  const [open, setOpen] = useState('');
  const cycleChapters =
    chapters
      ?.filter((chapter) => chapter.cycles.find((cycle) => cycle.id === child.cycle?.id))
      .filter((chapter) => chapter.field.id === selectedFieldId)
      .filter((chapter) => !workedChapters.find((workedChapter) => workedChapter.chapter.id === chapter.id)) || [];

  const groupedByMonth = workedChapters
    ?.filter((c) => c.chapter.field.id === selectedFieldId)
    .reduce<{ [key: string]: CommonWorkedChapter[] }>((acc, chapter) => {
      const month = `${new Date(chapter.createdAt).getMonth()}/${new Date(chapter.createdAt).getFullYear()}`;

      if (!acc[month]) {
        acc[month] = [];
      }

      acc[month].push(chapter);

      return acc;
    }, {});

  return (
    <div className={`${styles.wrapper} ${isParent ? styles.parent : ''}`}>
      <div
        className={`${styles.fieldTitle}`}
        style={{
          backgroundColor: getColorFromFieldId(selectedFieldId),
          boxShadow: `0px 3px 0 0 ${getColorFromFieldId(selectedFieldId)}80`,
        }}
      >
        {onBack && (
          <button
            onTouchStart={() => {
              haptic();
            }}
            onTouchEnd={() => {
              haptic();
            }}
            type="button"
            onClick={() => {
              onBack();
              setColor(null);
            }}
          >
            <img src={chevronLeft} alt="Back" />
          </button>
        )}
        <div className={styles.title}>{allFields.find((field) => field.id === selectedFieldId)?.name}</div>
        {onBack && <div className={styles.space} />}
      </div>

      {Object.values(groupedByMonth)
        ?.sort((a, b) => (new Date(a[0].createdAt) > new Date(b[0].createdAt) ? 1 : -1))
        .map((monthData, monthIndex) => (
          <div key={monthIndex}>
            <div
              className={styles.monthTitle}
              style={{
                backgroundColor: getColorFromFieldId(selectedFieldId),
              }}
            >
              {getMonthName(monthData[0].createdAt)}
            </div>
            <div className={styles.childrenWorkedChapters}>
              {(monthData as CommonWorkedChapter[])
                ?.sort((a, b) => (new Date(a.createdAt) > new Date(b.createdAt) ? 1 : -1))
                .map((chapter, index) => (
                  <ChildrenChapterBubble
                    key={chapter.id}
                    index={index}
                    chapter={chapter}
                    color={getColorFromFieldId(selectedFieldId)}
                    childrenId={child.id}
                    open={open === `${index}-${monthIndex}`}
                    openPopup={() => setOpen(`${index}-${monthIndex}` === open ? '' : `${index}-${monthIndex}`)}
                    isParent={isParent}
                  />
                ))}
            </div>
          </div>
        ))}
      {cycleChapters.length !== 0 && (
        <>
          <div
            className={`${styles.monthTitle} ${styles.notWorked}`}
            style={{
              backgroundColor: getColorFromFieldId(selectedFieldId),
            }}
          >
            Chapitres non travaillés
          </div>
          <div className={styles.childrenWorkedChapters}>
            {cycleChapters?.map((chapter, index) => (
              <ChildrenChapterBubble
                key={chapter.id}
                index={index}
                childrenId={child.id}
                color={getColorFromFieldId(selectedFieldId)}
                chapter={{
                  chapter,
                  elo: 0,
                  createdAt: new Date(),
                  id: 0,
                  tryCount: 0,
                  updatedAt: new Date(),
                }}
                notWorked
                open={open === `cycle-${index}`}
                openPopup={() => setOpen(`cycle-${index}` === open ? '' : `cycle-${index}`)}
                isParent={isParent}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
